/**
 * API calls of the operations dashboard (COO/CFO view), on the shared {@link opsClient}. Cross-shop:
 * no shop grant. Writes need no typed reason — they are the team's own management figures, audited
 * with a generated one (see `finance-inputs.service.ts`).
 */
import type {
  OpsBudgetInputDto,
  OpsBudgetListDto,
  OpsCashBalanceDto,
  OpsCashBalanceInputDto,
  OpsCashBalanceListDto,
  OpsCostItemDto,
  OpsCostItemInputDto,
  OpsCostItemListDto,
  OpsDeleteResultDto,
  OpsFinanceReportDto,
  OpsFxRateDto,
  OpsFxRateListDto,
  OpsPartnerStatusDto,
} from '../../../../libs/api-client/src/generated/models';
import { opsClient } from './ops-api';

// Re-exported from the generated client rather than restated here — see `support-api.ts`.
export type {
  OpsBudgetDto as OpsBudget,
  OpsCashBalanceDto as OpsCashBalance,
  OpsCohortDto as OpsCohort,
  OpsCostItemDto as OpsCostItem,
  OpsFinanceReportDto as OpsFinanceReport,
  OpsFxRateDto as OpsFxRate,
  OpsPartnerStatusDto as OpsPartnerStatus,
  OpsPartnerStreamDto as OpsPartnerStreamStatus,
  OpsPlanLineDto as OpsPlanLine,
  OpsPnlMonthDto as OpsPnlMonth,
  OpsSaasMonthDto as OpsSaasMonth,
  OpsUnitEconomicsMonthDto as OpsUnitEconomicsMonth,
} from '../../../../libs/api-client/src/generated/models';

/** Query keys, so a write can invalidate exactly what it changed. */
export const OPERATIONS_QUERY_KEYS = {
  report: ['ops-operations', 'report'] as const,
  costs: ['ops-operations', 'costs'] as const,
  budgets: ['ops-operations', 'budgets'] as const,
  cash: ['ops-operations', 'cash'] as const,
  fx: ['ops-operations', 'fx'] as const,
  partner: ['ops-operations', 'partner'] as const,
};

/** The whole report for a range. */
export async function fetchFinanceReport(
  from: string,
  to: string,
): Promise<OpsFinanceReportDto> {
  const { data } = await opsClient().getOpsFinanceReport({ from, to });
  return data;
}

/** Partner API mirror status. */
export async function fetchPartnerStatus(): Promise<OpsPartnerStatusDto> {
  const { data } = await opsClient().getOpsPartnerSyncStatus();
  return data;
}

/** Starts a Partner API pull; false when unconfigured or already running. */
export async function startPartnerSync(): Promise<boolean> {
  const { data } = await opsClient().startOpsPartnerSync();
  return data.started;
}

/** Every cost item. */
export async function fetchCostItems(): Promise<OpsCostItemListDto> {
  const { data } = await opsClient().listOpsCostItems();
  return data;
}

/** Creates a cost item. */
export async function createCostItem(
  input: OpsCostItemInputDto,
): Promise<OpsCostItemDto> {
  const { data } = await opsClient().createOpsCostItem({
    opsCostItemInputDto: input,
  });
  return data;
}

/** Replaces a cost item. */
export async function updateCostItem(
  id: string,
  input: OpsCostItemInputDto,
): Promise<OpsCostItemDto> {
  const { data } = await opsClient().updateOpsCostItem({
    id,
    opsCostItemInputDto: input,
  });
  return data;
}

/** Deletes a cost item. */
export async function deleteCostItem(id: string): Promise<OpsDeleteResultDto> {
  const { data } = await opsClient().deleteOpsCostItem({ id });
  return data;
}

/** Plan figures of a year. */
export async function fetchBudgets(year: number): Promise<OpsBudgetListDto> {
  const { data } = await opsClient().listOpsBudgets({ year });
  return data;
}

/** Sets or clears one plan figure; answers the whole year. */
export async function setBudget(
  input: OpsBudgetInputDto,
): Promise<OpsBudgetListDto> {
  const { data } = await opsClient().setOpsBudget({ opsBudgetInputDto: input });
  return data;
}

/** Every cash balance. */
export async function fetchCashBalances(): Promise<OpsCashBalanceListDto> {
  const { data } = await opsClient().listOpsCashBalances();
  return data;
}

/** Records a cash balance. */
export async function createCashBalance(
  input: OpsCashBalanceInputDto,
): Promise<OpsCashBalanceDto> {
  const { data } = await opsClient().createOpsCashBalance({
    opsCashBalanceInputDto: input,
  });
  return data;
}

/** Deletes a cash balance. */
export async function deleteCashBalance(
  id: string,
): Promise<OpsDeleteResultDto> {
  const { data } = await opsClient().deleteOpsCashBalance({ id });
  return data;
}

/** Every stored rate. */
export async function fetchFxRates(): Promise<OpsFxRateListDto> {
  const { data } = await opsClient().listOpsFxRates();
  return data;
}

/** Overrides a month's rate. */
export async function setFxRate(
  month: string,
  usdPerEur: number,
): Promise<OpsFxRateDto> {
  const { data } = await opsClient().setOpsFxRate({
    month,
    opsFxRateInputDto: { usdPerEur },
  });
  return data;
}

/** Removes a month's rate. */
export async function clearFxRate(month: string): Promise<OpsDeleteResultDto> {
  const { data } = await opsClient().clearOpsFxRate({ month });
  return data;
}
